import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, User, Mail, Phone, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

const applianceTypes = [
  { value: 'laveuse', label: 'Laveuse' },
  { value: 'secheuse', label: 'Sécheuse' },
  { value: 'lave-vaisselle', label: 'Lave-vaisselle' },
  { value: 'cuisiniere', label: 'Cuisinière' },
  { value: 'refrigerateur', label: 'Réfrigérateur' },
  { value: 'congelateur', label: 'Congélateur' },
  { value: 'autre', label: 'Autre' }
];

export const PartOrderPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    appliance_type: '',
    brand: '',
    model: '',
    part_description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/part-orders`, formData);
      setSubmitted(true);
      toast.success('Votre demande a été envoyée avec succès!');
    } catch (error) {
      console.error('Error submitting part order:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-[#1a3a5c] mb-4">
            Demande envoyée!
          </h1>
          <p className="text-slate-600 mb-8">
            Nous avons bien reçu votre demande de pièce. Notre équipe vous contactera 
            sous peu pour confirmer la disponibilité et le prix.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button variant="outline" className="rounded-full px-8">
                Retour à l'accueil
              </Button>
            </Link>
            <Link to="/services">
              <Button className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] px-8">
                Voir nos services
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-[#00b4d8] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/services#vente-pieces" className="inline-flex items-center text-slate-300 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux services
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
              <Package className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Commander une pièce
            </h1>
          </div>
          <p className="text-lg text-slate-300 max-w-2xl">
            Remplissez le formulaire ci-dessous et notre équipe vous contactera pour confirmer 
            la disponibilité et le prix de la pièce recherchée.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                  <User className="w-5 h-5 text-[#00b4d8]" />
                  Vos coordonnées
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Prénom *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nom *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Courriel *</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="pl-10"
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone">Téléphone *</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="pl-10"
                        placeholder="(450) 555-1234"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Appliance Info */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#00b4d8]" />
                  Information sur l'appareil
                </h3>
                <div>
                  <Label htmlFor="appliance_type">Type d'appareil *</Label>
                  <Select 
                    value={formData.appliance_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, appliance_type: value }))}
                    required
                  >
                    <SelectTrigger className="mt-1" data-testid="appliance-type-select">
                      <SelectValue placeholder="Sélectionnez un type d'appareil" />
                    </SelectTrigger>
                    <SelectContent>
                      {applianceTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="brand">Marque</Label>
                    <Input
                      id="brand"
                      name="brand"
                      value={formData.brand}
                      onChange={handleChange}
                      placeholder="Ex: Samsung, LG, Whirlpool..."
                      className="mt-1"
                      data-testid="brand-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="model">Modèle</Label>
                    <Input
                      id="model"
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      placeholder="Numéro de modèle si connu"
                      className="mt-1"
                      data-testid="model-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="part_description">Description de la pièce recherchée *</Label>
                  <Textarea
                    id="part_description"
                    name="part_description"
                    value={formData.part_description}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Décrivez la pièce que vous recherchez (nom, numéro de pièce si connu, symptômes du problème...)"
                    className="mt-1"
                    data-testid="part-description-input"
                  />
                </div>
              </div>

              <p className="text-sm text-slate-500">
              En transmettant ce formulaire, vous consentez à l’utilisation de vos renseignements pour traiter votre demande. Consultez notre <a href="/politique-confidentialite" className="text-[#0077b6] underline">politique de confidentialité</a>.
            </p>

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white py-6 text-lg"
                data-testid="submit-part-order-btn"
              >
                {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
              </Button>

              <p className="text-center text-sm text-slate-500">
                Nous vous contacterons dans les plus brefs délais pour confirmer 
                la disponibilité et le prix.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartOrderPage;
